import type { FlowVariant } from "../funnel/types";
import { resolveFlowFromUrlOrNull } from "../funnel/resolveFlowFromUrl";

export const FLOW_STORAGE_KEY = "vireo:funnel:active-flow";
export const FUNNEL_SESSION_STORAGE_KEY = "sehaj:funnel:session-id";

const createFallbackUuid = (): string => {
  const bytes = Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = bytes.map((byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
};

export const getOrCreateFunnelSessionId = (): string => {
  if (typeof window === "undefined") return "server";
  try {
    const existing = window.sessionStorage.getItem(FUNNEL_SESSION_STORAGE_KEY)?.trim();
    if (existing) return existing;
    const created = globalThis.crypto?.randomUUID?.() ?? createFallbackUuid();
    window.sessionStorage.setItem(FUNNEL_SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    return globalThis.crypto?.randomUUID?.() ?? createFallbackUuid();
  }
};

type InitialFlowResolution = {
  flow: FlowVariant;
  shouldPersistFlow: boolean;
};

export const resolveInitialFlowState = (
  urlLike: URL | string,
  storedFlow: FlowVariant | null,
): InitialFlowResolution => {
  const url = typeof urlLike === "string" ? new URL(urlLike) : urlLike;
  const explicitFlow = resolveFlowFromUrlOrNull(url);

  if (explicitFlow) {
    return { flow: explicitFlow, shouldPersistFlow: true };
  }

  const isCheckoutReturn = Boolean(url.searchParams.get("session_id")?.trim());
  if (isCheckoutReturn && storedFlow) {
    return { flow: storedFlow, shouldPersistFlow: false };
  }

  return { flow: "a", shouldPersistFlow: false };
};

export const buildEmbeddedStepPageviewPayload = (
  currentUrl: string,
  flow: FlowVariant,
  stepId: string,
  stepIndex: number,
): Record<string, string | number | boolean> => {
  const url = new URL(currentUrl);
  return {
    $current_url: `${url.origin}${url.pathname}${url.search}#${stepId}`,
    flow_variant: flow,
    funnel_flow: flow,
    funnel_hash_route: `#${stepId}`,
    funnel_step_id: stepId,
    funnel_step_index: stepIndex,
    funnel_embedded_step: true,
  };
};
